import { useMemo } from "react";
import { useGetIdentity, useGetList } from "ra-core";
import { Filter, User as UserIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { MemberAvatar } from "../shared/MemberAvatar";
import type { Appointment, Sale } from "../types";

// Sprint 8.1 — Toolbar /appointments conforme cal-direction1.jsx Toolbar.
// Member chips avec count par membre (cette semaine) + "Mes RDV uniquement"
// + chips types (RDV client / Interne / Personnel).

export type AppointmentTypeFilter = "client" | "interne" | "personnel";

interface AppointmentToolbarProps {
  hiddenSaleIds: Set<number>;
  onToggleSale: (id: number) => void;
  mineOnly: boolean;
  onMineOnlyToggle: () => void;
  typeFilters: Set<AppointmentTypeFilter>;
  onTypeToggle: (t: AppointmentTypeFilter) => void;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function AppointmentToolbar({
  hiddenSaleIds,
  onToggleSale,
  mineOnly,
  onMineOnlyToggle,
  typeFilters,
  onTypeToggle,
}: AppointmentToolbarProps) {
  const { identity } = useGetIdentity();
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });

  // Charger appointments de la semaine pour les counts membre
  const startOfWeek = useMemo(() => {
    const d = new Date();
    const dayNr = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dayNr);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);
  const endOfWeek = useMemo(() => {
    return new Date(Date.now() + WEEK_MS).toISOString();
  }, []);

  const { data: weekAppts } = useGetList<Appointment>("appointments", {
    pagination: { page: 1, perPage: 500 },
    filter: {
      "start_at@gte": startOfWeek,
      "start_at@lte": endOfWeek,
    },
  });

  const countBySale = useMemo(() => {
    const map = new Map<number, number>();
    (weekAppts ?? []).forEach((a) => {
      if (a.sales_id == null) return;
      const k = Number(a.sales_id);
      map.set(k, (map.get(k) ?? 0) + 1);
    });
    return map;
  }, [weekAppts]);

  const meId = identity?.id != null ? Number(identity.id) : undefined;

  const TYPE_OPTIONS: {
    key: AppointmentTypeFilter;
    label: string;
    dot: string;
  }[] = [
    { key: "client", label: "RDV client", dot: "bg-violet-500" },
    { key: "interne", label: "Interne", dot: "bg-blue-500" },
    { key: "personnel", label: "Personnel", dot: "bg-muted-foreground" },
  ];

  return (
    <div className="flex items-center gap-2 flex-wrap rounded-xl border border-border bg-card px-3 py-2.5">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mr-1">
        Équipe
      </span>

      {/* Member chips avec counts */}
      {(sales ?? []).map((s) => {
        const id = Number(s.id);
        const hidden = hiddenSaleIds.has(id);
        const count = countBySale.get(id) ?? 0;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onToggleSale(id)}
            aria-pressed={!hidden}
            className={cn(
              "inline-flex items-center gap-1.5 h-7 pl-1 pr-2.5 rounded-full border text-[12px] transition-colors",
              hidden
                ? "bg-muted/40 border-border text-muted-foreground/70 line-through"
                : "bg-card border-border text-foreground hover:bg-muted/40",
            )}
          >
            <MemberAvatar salesId={id} size={20} />
            <span className="font-medium">
              {s.first_name ?? ""} {s.last_name ?? ""}
            </span>
            <span className="text-[10.5px] tabular-nums text-muted-foreground">
              {count}
            </span>
          </button>
        );
      })}

      <div className="w-px h-4 bg-border mx-1 shrink-0" />

      {/* Mes RDV uniquement */}
      <button
        type="button"
        onClick={onMineOnlyToggle}
        aria-pressed={mineOnly}
        disabled={meId == null}
        className={cn(
          "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-[12px] transition-colors shrink-0",
          mineOnly
            ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900/40 dark:text-emerald-200"
            : "bg-card border-border text-muted-foreground hover:bg-muted/40 hover:text-foreground",
        )}
      >
        <UserIcon className="size-3" />
        Mes RDV uniquement
      </button>

      <div className="w-px h-4 bg-border mx-1 shrink-0" />

      {/* Type chips */}
      {TYPE_OPTIONS.map((t) => {
        const active = typeFilters.has(t.key);
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onTypeToggle(t.key)}
            aria-pressed={active}
            className={cn(
              "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-[12px] transition-colors shrink-0",
              active
                ? "bg-card border-border text-foreground"
                : "bg-card border-border/60 text-muted-foreground hover:bg-muted/40",
            )}
          >
            <span className={cn("size-1.5 rounded-full", t.dot)} />
            {t.label}
          </button>
        );
      })}

      <div className="ml-auto" />

      <button
        type="button"
        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] text-muted-foreground hover:text-foreground hover:bg-muted/40"
      >
        <Filter className="size-3" />
        Plus de filtres
      </button>
    </div>
  );
}
